import React, { useEffect, useState } from "react"
import { Box, Dialog, DialogContent, styled, Typography, IconButton, Grid } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { Col, Row } from "react-bootstrap"
import { useDispatch } from "react-redux"
import { useTheme } from "@mui/system"

import AppButton from "components/StyledComponents/AppButton"
import AppTextField from "components/StyledComponents/AppTextField"
import AppTextArea from "components/StyledComponents/AppTextArea"
import ImageUploadField from "components/FileUpload/ImageUploadField"
import ImageCropper from "components/ImageCropper"
import AppDatePicker from "components/AppDatePicker"
import StyledLabel from "components/StyledComponents/StyledLabel"

import { uploadNotificationImageAction } from "redux/superAdmin/actions/user"
import { superAdminAction } from "redux/superAdmin/slice/user"

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    backgroundColor: "transparent",
    maxWidth: 750,
    width: "100%"
  },
  "& .MuiDialogContent-root": {
    backgroundColor: theme.palette.background.default,
    borderRadius: 12,
    border: "none",
    width: "100%",
    padding: 24,
    boxSizing: "border-box"
  }
}))

const NotifyForm = ({ formik, open = false, onClose = () => {} }) => {
  const theme = useTheme()
  const dispatch = useDispatch()

  const [uploadType, setUploadType] = useState("")
  const [openCrop, setCrop] = useState(false)
  const [fileDetails, setFileDetails] = useState(null)
  const [imageObjectURL, setImageObjectUrl] = useState(null)

  useEffect(() => {
    if (open) {
      formik.resetForm({
        notificationName: "",
        uploadThumbnail: "",
        scheduleNotificationDate: "",
        scheduleNotificationTime: "",
        employeeClass: ["All"],
        notificationContent: ""
      })
      dispatch(superAdminAction.setNotificationImageId(""))
      dispatch(superAdminAction.setNotificationData({}))
    }
  }, [open])

  const handleImageUpload = (image) => {
    if (image) {
      setUploadType("uploadThumbnail")
      setCrop(true)
      setFileDetails(image)
      setImageObjectUrl(URL.createObjectURL(image))
    }
  }

  const handleCroppedImage = (image) => {
    if (uploadType === "uploadThumbnail") {
      const data = new FormData()
      data.append("media", image)
      data.append("media_type", "IMAGE")
      data.append("content_type", image.type)

      formik.setFieldValue("uploadThumbnail", image)
      dispatch(uploadNotificationImageAction(data))
      setUploadType("")
    }
  }

  const handleImageDelete = () => formik.setFieldValue("uploadThumbnail", null)

  return (
    <StyledDialog open={open}>
      <DialogContent>
        {openCrop && (
          <ImageCropper
            setSelectedImage={handleCroppedImage}
            srcImage={imageObjectURL}
            fileDetail={fileDetails}
            aspectRatio={1}
            onClose={setCrop}
            openCrop={openCrop}
          />
        )}

        <Box className="d-flex justify-content-end">
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.text.primary,
              position: "absolute",
              top: "10px",
              right: "10px",
              backgroundColor: theme.palette.background.main
            }}>
            <CloseIcon sx={{ height: 14, width: 14 }} />
          </IconButton>
        </Box>

        <Box className="w-100 d-flex justify-content-center mb-2">
          <Typography variant="h2">Add New Notification</Typography>
        </Box>

        <Box flexDirection="column" className="mt-4">
          <Row className="w-100 m-0">
            <Col lg="6">
              <AppTextField
                id="notificationName"
                name="notificationName"
                label="Notification Name"
                isUpperCase
                fullWidth
                isRequired
                placeholder="Enter Notification Name"
                value={formik.values.notificationName}
                onChange={formik.handleChange}
                error={formik.touched.notificationName && Boolean(formik.errors.notificationName)}
                helperText={formik.touched.notificationName && formik.errors.notificationName}
              />
            </Col>
            <Col lg="6">
              <ImageUploadField
                label="Upload Thumbnail"
                isVariant
                placeholder="Upload Image"
                value={formik.values.uploadThumbnail}
                onChange={handleImageUpload}
                onDelete={handleImageDelete}
                error={formik.errors.uploadThumbnail}
                touched={formik.touched.uploadThumbnail}
              />
            </Col>
          </Row>

          <Row className="w-100 m-0 mt-4">
            <Col lg="6">
              <Grid item className="datesContainer">
                <StyledLabel variant="body1Regular">
                  Schedule Notification Date <span>*</span>
                </StyledLabel>
                <Box className="dateFieldContainer w-100 mt-2">
                  <AppDatePicker
                    minDate={new Date()}
                    maxDate={new Date().setMonth(new Date().getMonth() + 5)}
                    inputFormat="dd MMM yyyy"
                    disableMaskedInput
                    value={formik.values.scheduleNotificationDate}
                    onChange={(val) => formik.setFieldValue("scheduleNotificationDate", val)}
                    error={formik.errors.scheduleNotificationDate}
                    touched={formik.touched.scheduleNotificationDate}
                  />
                </Box>
              </Grid>
            </Col>
            <Col lg="6">
              <AppTextField
                type="time"
                fullWidth
                label="Schedule Notification Time"
                isRequired
                isUpperCase
                value={formik.values.scheduleNotificationTime}
                onChange={formik.handleChange("scheduleNotificationTime")}
                error={
                  formik.touched.scheduleNotificationTime &&
                  Boolean(formik.errors.scheduleNotificationTime)
                }
                helperText={
                  formik.touched.scheduleNotificationTime && formik.errors.scheduleNotificationTime
                }
              />
            </Col>
          </Row>

          <Row className="w-100 m-0 mt-4">
            <Col lg="12">
              <AppTextArea
                label="Notification Content"
                minRows={3}
                maxRows={15}
                isRequired
                placeholder="Enter Notification Content"
                value={formik.values.notificationContent}
                onChange={formik.handleChange("notificationContent")}
                error={formik.errors.notificationContent}
                touched={formik.touched.notificationContent}
              />
            </Col>
          </Row>
        </Box>

        <Box className="mt-5 d-flex justify-content-around">
          <AppButton onClick={formik.handleSubmit} sx={{ height: "48px", padding: "10px 50px" }}>
            Send
          </AppButton>
        </Box>
      </DialogContent>
    </StyledDialog>
  )
}

export default NotifyForm
